import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import getAllStore from "../api/store/getAllStore";
import { AuthContext } from "../context/AuthContext";
import api from "../api/api";
import verifyToken from "../api/user/verifyToken";
import { CITIES } from "../data/CITIES";
import ReservationStep1 from "../component/reservationPageV2/ReservationStep1";
import ReservationStep2 from "../component/reservationPageV2/ReservationStep2";
import ReservationStep3 from "../component/reservationPageV2/ReservationStep3";
import getTable from "../api/store/getTable";
import { useNavigate } from "react-router-dom";
import FadeWrapper from "../component/FadeWrapper";
import backIcon from "../icons/page_back.png";
import { isReservationStoreVisible } from "../storeVisibility";

const IDLE_TIMEOUT = 20 * 60 * 1000; // 20 minutes

const ReservationPage = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const theme = useTheme();
  const [cities, setCities] = useState([]);
  const [step, setStep] = useState(1);
  const [data, setData] = useState({ table: [] });
  const [idleDialogOpen, setIdleDialogOpen] = useState(false);
  const { user, setUser } = useContext(AuthContext);
  const idleTimer = useRef(null);
  const selectedStoreCode = data.store?.Code;

  const resetIdleTimer = useCallback(() => {
    window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => {
      setIdleDialogOpen(true); // Show dialog after timeout
    }, IDLE_TIMEOUT);
  }, []);

  const refreshPage = () => {
    window.location.reload();
  };

  useEffect(() => {
    if (user) {
      return undefined;
    }
    const storedToken = localStorage.getItem("token");
    if (!storedToken) {
      return undefined;
    }

    let cancelled = false;
    const fetchUser = async () => {
      try {
        const responseData = await verifyToken();
        if (cancelled) {
          return;
        }
        if (responseData.code === "000") {
          setUser(responseData.user);
          return;
        }
        setUser(null);
        localStorage.removeItem("token");
        api.defaults.headers.common["Authorization"] = "";
      } catch (err) {
        if (!cancelled) {
          setUser(null);
          localStorage.removeItem("token");
          api.defaults.headers.common["Authorization"] = "";
        }
      }
    };

    fetchUser();
    return () => {
      cancelled = true;
    };
  }, [setUser, user]);

  useEffect(() => {
    const handleUserActivity = () => {
      resetIdleTimer();
    };

    // Add event listeners for user activity
    window.addEventListener("mousemove", handleUserActivity);
    window.addEventListener("keydown", handleUserActivity);

    // Start the idle timer
    resetIdleTimer();

    return () => {
      // Clean up event listeners and timer
      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("keydown", handleUserActivity);
      window.clearTimeout(idleTimer.current);
    };
  }, [resetIdleTimer]);

  useEffect(() => {
    let tmpCity = {};
    let cityArray = [];
    for (var key in stores) {
      tmpCity[stores[key].City] = 1;
    }
    for (key in tmpCity) {
      cityArray.push(key);
    }

    cityArray.sort((a, b) => {
      return CITIES.indexOf(a) - CITIES.indexOf(b);
    });

    setCities(cityArray);
  }, [stores]);

  useEffect(() => {
    if (step !== 1) {
      return;
    }
    const fetchStores = async () => {
      try {
        const responseData = await getAllStore();
        if (responseData.code === "000") {
          const openStores = responseData.stores.filter((store) =>
            isReservationStoreVisible(store, user)
          );
          setStores(openStores);
          return;
        }
        setStores(null);
      } catch (err) {
        console.error(err);
      }
    };
    fetchStores();
  }, [step, user]);

  useEffect(() => {
    if (step !== 2 || !selectedStoreCode) {
      return;
    }
    const fetchTables = async () => {
      try {
        const responseData = await getTable(selectedStoreCode);
        if (responseData.code === "000") {
          responseData.table.sort((a, b) => {
            const numA = parseInt(a.Name, 10);
            const numB = parseInt(b.Name, 10);
            return numA - numB;
          });
          const sortedTables = responseData.table.sort((a, b) =>
            a.Code.localeCompare(b.Code)
          );
          setData((current) => ({ ...current, table: sortedTables }));
          return;
        }
        setData((current) => ({ ...current, table: [] }));
      } catch (err) {
        console.error(err);
      }
    };
    fetchTables();
  }, [selectedStoreCode, step]);

  const nextStep = (newData) => {
    setData({ ...data, ...newData });
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleBackButtonClick = () => {
    switch (step) {
      case 1:
        navigate("/");
        break;
      case 2:
      case 3:
        prevStep();
        break;
      default:
        navigate("/");
        break;
    }
  };

  return (
    <FadeWrapper>
      <Box width="100%" bgcolor={theme.palette.background.default}>
        <AppBar
          sx={{ bgcolor: theme.palette.appBar }}
          position="sticky"
          color="secondary"
        >
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={backIcon}
              alt="back"
              sx={{ width: "50px", marginLeft: "-16px" }}
              onClick={handleBackButtonClick}
            />
            <Typography
              align="center"
              sx={{
                fontWeight: "bold",
                fontSize: "h6.fontSize",
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            >
              {step === 1 && "選擇店家"}
              {step === 2 && data.store.Name}
            </Typography>
            <Box sx={{ width: "50px" }} />
          </Toolbar>
        </AppBar>
        <Box m={1} />
        <Box width="100vw" minHeight="100vh" position="relative">
          {step === 1 && (
            <ReservationStep1
              categories={cities}
              stores={stores}
              nextStep={nextStep}
            />
          )}
          {step === 2 && (
            <ReservationStep2
              nextStep={nextStep}
              prevStep={prevStep}
              tables={data.table}
              store={data.store}
            />
          )}
          {step === 3 && <ReservationStep3 order={data.order} />}
        </Box>
      </Box>
      <Dialog open={idleDialogOpen} onClose={() => {}} >
        <DialogTitle>通知</DialogTitle>
        <DialogContent>
          <DialogContentText>
            你在此閒置超過20分鐘，請重新整理頁面。
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{justifyContent:"center"}}>
          <Button onClick={refreshPage} color="primary" variant="contained">
            重新整理
          </Button>

        </DialogActions>
      </Dialog>
    </FadeWrapper>
  );
};

export default ReservationPage;
